import { useRef } from 'react';

import type { ICellRendererParams } from '@mint-lib/ag-grid/community';
import { LoadingCell } from '@mint-lib/ag-grid/components';
import { useTranslation } from '@mint-lib/i18n';
import {
  Chip,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TruncateText,
} from '@mint-lib/tw-components';

import type { UserTableV2Row } from '../datasource.js';
import {
  getUserTags,
  isUserUpdating,
  type UserTableV2GridContext,
} from '../gridContext.js';
import useLabelChipFit from '../hooks/useLabelChipFit.js';

type UserLabel = NonNullable<UserTableV2Row['tags']>[number];
type Props = ICellRendererParams<
  UserTableV2Row,
  UserLabel[] | undefined,
  UserTableV2GridContext
> & { onViewAllLabels?: (user: UserTableV2Row) => void };

const LabelCell = ({ context, data, value, onViewAllLabels }: Props) => {
  const { t } = useTranslation('@myn/permissions');
  const isUpdating = isUserUpdating(context, data?.id);

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const labels = getUserTags(context, data?.id) ?? value ?? data?.tags ?? [];
  const visibleCount = useLabelChipFit({
    totalChips: labels.length,
    containerRef,
    measureRef,
  });

  if (!data || isUpdating || context?.isUserTagsLoading) {
    return <LoadingCell />;
  }

  if (!labels.length) {
    return null;
  }

  const overflowCount = Math.max(labels.length - visibleCount, 0);

  return (
    <div
      ref={containerRef}
      className="relative flex h-full min-w-0 items-center gap-1 overflow-hidden"
    >
      {labels.slice(0, visibleCount).map((label) => (
        <Chip
          key={label.id}
          size="small"
          color="silver"
          className="max-w-[120px] shrink-0"
        >
          <TruncateText>{label.name}</TruncateText>
        </Chip>
      ))}
      {overflowCount > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Chip
              size="small"
              color="silver"
              role="button"
              tabIndex={0}
              aria-label={t('View all labels')}
              className="shrink-0 cursor-pointer"
              onClick={(event) => {
                event.stopPropagation();
                onViewAllLabels?.(data);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                event.stopPropagation();
                onViewAllLabels?.(data);
              }}
            >
              +{overflowCount}
            </Chip>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={8}>
            {t('View all labels')}
          </TooltipContent>
        </Tooltip>
      ) : null}

      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute top-0 left-0 flex gap-1"
      >
        {labels.map((label) => (
          <Chip
            key={`measure-${label.id}`}
            size="small"
            color="silver"
            className="max-w-[120px] shrink-0"
          >
            <TruncateText>{label.name}</TruncateText>
          </Chip>
        ))}
      </div>
    </div>
  );
};

export default LabelCell;
